import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../lib/authFetch';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Percent, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  RefreshCw,
  Play,
  BarChart3,
  Wallet,
  Activity,
  Clock,
  ChevronRight,
  Shield,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import Header from '../components/Header';
import { StressMonthSimulator } from '../components/StressMonthSimulator';
import { useToast } from '../hooks/use-toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Format currency
const formatCurrency = (value) => {
  if (value >= 1000000000) return `$${(value / 1000000000).toFixed(2)}B`;
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
};

// Format percentage
const formatPercent = (value) => `${(value * 100).toFixed(1)}%`;

// Portfolio risk colors
const getRiskColor = (portfolioType) => {
  const colors = {
    bedrock: 'bg-emerald-500',
    fortress: 'bg-sky-500',
    horizon: 'bg-amber-500',
    velocity: 'bg-orange-500',
    summit: 'bg-rose-500',
    nebula: 'bg-violet-500',
  };
  return colors[portfolioType] || 'bg-gray-500';
};

// Alert severity colors
const getAlertColor = (severity) => {
  const colors = {
    CRITICAL: 'bg-red-500 text-white',
    HIGH: 'bg-orange-500 text-white',
    WARNING: 'bg-amber-500 text-black',
    INFO: 'bg-blue-500 text-white',
  };
  return colors[severity] || 'bg-gray-500 text-white';
};

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [portfolios, setPortfolios] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [pendingRebalancing, setPendingRebalancing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [runningCrossing, setRunningCrossing] = useState(false);
  const [runningPredictions, setRunningPredictions] = useState(false);
  const [stressOpen, setStressOpen] = useState(false);
  const { toast } = useToast();

  // Fetch all Helix data
  const fetchData = useCallback(async () => {
    try {
      const [dashboardRes, portfoliosRes, predictionsRes, rebalancingRes] = await Promise.all([
        authFetch(`${API_BASE}/api/lms/dashboard`),
        authFetch(`${API_BASE}/api/lms/portfolios`),
        authFetch(`${API_BASE}/api/lms/predictions`),
        authFetch(`${API_BASE}/api/lms/rebalancing/pending`),
      ]);

      const [dashboard, portfolioData, predictionData, rebalancingData] = await Promise.all([
        dashboardRes.json(),
        portfoliosRes.json(),
        predictionsRes.json(),
        rebalancingRes.json(),
      ]);

      setDashboardData(dashboard);
      setPortfolios(portfolioData);
      setPredictions(predictionData);
      setPendingRebalancing(rebalancingData);
    } catch (error) {
      console.error('Error fetching Helix data:', error);
      toast({ title: 'Error', description: 'Failed to fetch Helix data', variant: 'destructive' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleRunCrossing = async () => {
    setRunningCrossing(true);
    try {
      const res = await authFetch(`${API_BASE}/api/lms/run-daily-crossing`, { method: 'POST' });
      const result = await res.json();
      if (res.ok) {
        toast({ title: 'Crossing Complete', description: `System efficiency: ${formatPercent(result.systemCrossingEfficiency)}` });
        fetchData();
      } else {
        throw new Error(result.detail || 'Failed to run crossing');
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setRunningCrossing(false);
    }
  };

  const handleGeneratePredictions = async () => {
    setRunningPredictions(true);
    try {
      const res = await authFetch(`${API_BASE}/api/lms/generate-predictions`, { method: 'POST' });
      const result = await res.json();
      if (res.ok) {
        const successCount = result.results.filter(r => r.status === 'success').length;
        toast({ title: 'Predictions Generated', description: `${successCount}/6 portfolios analyzed` });
        fetchData();
      } else {
        throw new Error(result.detail || 'Failed to generate predictions');
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setRunningPredictions(false);
    }
  };

  const handleApproveRebalancing = async (actionId) => {
    try {
      const res = await authFetch(`${API_BASE}/api/lms/rebalancing/${actionId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved_by: 'admin' }),
      });
      if (res.ok) {
        toast({ title: 'Approved', description: 'Rebalancing action approved successfully' });
        setActionDialogOpen(false);
        fetchData();
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to approve');
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleRejectRebalancing = async (actionId) => {
    try {
      const res = await authFetch(`${API_BASE}/api/lms/rebalancing/${actionId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved_by: 'admin' }),
      });
      if (res.ok) {
        toast({ title: 'Rejected', description: 'Rebalancing action rejected' });
        setActionDialogOpen(false);
        fetchData();
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to reject');
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading Helix Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header 
        title="trove helix." 
        subtitle="Continuous rebalancing. Compounded outcomes." 
      />
      
      <div className="p-6">
        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 mb-6">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleRunCrossing} disabled={runningCrossing}>
            <Play className={`h-4 w-4 mr-2 ${runningCrossing ? 'animate-pulse' : ''}`} />
            Run Crossing
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setStressOpen(true)}
            className="border-red-200 text-red-700 hover:bg-red-50"
            data-testid="stress-month-btn"
          >
            <Zap className="h-4 w-4 mr-2" />
            Simulate Stress Month
          </Button>
          <Button size="sm" onClick={handleGeneratePredictions} disabled={runningPredictions} className="bg-[#1a1a3e] hover:bg-[#2a2a5e]">
            <BarChart3 className={`h-4 w-4 mr-2 ${runningPredictions ? 'animate-pulse' : ''}`} />
            Generate Predictions
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total AUM</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[#1a1a3e]">{formatCurrency(dashboardData?.summary?.totalAUM || 0)}</div>
              <p className="text-xs text-muted-foreground mt-1">Across {dashboardData?.summary?.portfolioCount || 0} portfolios</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Liquid Buffer</CardTitle>
              <Wallet className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[#1a1a3e]">{formatCurrency(dashboardData?.summary?.totalLiquidBuffer || 0)}</div>
              <p className="text-xs text-muted-foreground mt-1">{formatPercent(dashboardData?.summary?.avgLiquidPercentage || 0)} average</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Crossing Efficiency</CardTitle>
              <Percent className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-600">{formatPercent(dashboardData?.summary?.systemCrossingEfficiency || 0)}</div>
              <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-emerald-500 rounded-full transition-all"
                  style={{ width: `${(dashboardData?.summary?.systemCrossingEfficiency || 0) * 100}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Target: 80%</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Pending Actions</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[#1a1a3e]">{pendingRebalancing.length}</div>
              <p className="text-xs text-muted-foreground mt-1">Rebalancing awaiting approval</p>
            </CardContent>
          </Card>
        </div>

        {/* Alerts */}
        {dashboardData?.alerts?.length > 0 && (
          <Card className="mb-6 border-l-4 border-l-amber-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                System Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {dashboardData.alerts.map((alert, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <Badge className={getAlertColor(alert.severity)}>{alert.severity}</Badge>
                    <span className="text-sm">{alert.message}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content Tabs */}
        <Tabs defaultValue="portfolios" className="space-y-6">
          <TabsList className="bg-muted/50">
            <TabsTrigger value="portfolios">Portfolios</TabsTrigger>
            <TabsTrigger value="predictions">Predictions</TabsTrigger>
            <TabsTrigger value="rebalancing">Rebalancing</TabsTrigger>
          </TabsList>

          {/* Portfolios Tab */}
          <TabsContent value="portfolios">
            <Card>
              <CardHeader>
                <CardTitle>Portfolio Overview</CardTitle>
                <CardDescription>Real-time metrics for all 6 investment portfolios</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Portfolio</TableHead>
                      <TableHead className="text-right">AUM</TableHead>
                      <TableHead className="text-right">Liquid %</TableHead>
                      <TableHead className="text-right">Target</TableHead>
                      <TableHead className="text-right">Drift</TableHead>
                      <TableHead className="text-right">Tier 1</TableHead>
                      <TableHead className="text-right">Tier 2</TableHead>
                      <TableHead className="text-right">Tier 3</TableHead>
                      <TableHead className="text-right">Coverage</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {portfolios.map((portfolio) => (
                      <TableRow key={portfolio.portfolioType}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-8 rounded-full ${getRiskColor(portfolio.portfolioType)}`} />
                            <div>
                              <div className="font-medium">{portfolio.name}</div>
                              <div className="text-xs text-muted-foreground capitalize">{portfolio.portfolioType}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(portfolio.totalAUM)}</TableCell>
                        <TableCell className="text-right">{formatPercent(portfolio.metrics?.liquidPercentage || 0)}</TableCell>
                        <TableCell className="text-right text-muted-foreground">{formatPercent(portfolio.targetAllocation?.liquid || 0)}</TableCell>
                        <TableCell className="text-right">
                          <span className={Math.abs(portfolio.metrics?.driftFromTarget || 0) > 0.03 ? 'text-amber-600 font-medium' : 'text-emerald-600'}>
                            {portfolio.metrics?.driftFromTarget > 0 ? '+' : ''}{formatPercent(portfolio.metrics?.driftFromTarget || 0)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-sm">{formatPercent(portfolio.metrics?.tier1Percentage || 0)}</TableCell>
                        <TableCell className="text-right text-sm">{formatPercent(portfolio.metrics?.tier2Percentage || 0)}</TableCell>
                        <TableCell className="text-right text-sm">{formatPercent(portfolio.metrics?.tier3Percentage || 0)}</TableCell>
                        <TableCell className="text-right">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                            (portfolio.metrics?.daysOfCoverage || 0) > 90 ? 'bg-emerald-100 text-emerald-700' :
                            (portfolio.metrics?.daysOfCoverage || 0) > 30 ? 'bg-sky-100 text-sky-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {Math.round(portfolio.metrics?.daysOfCoverage || 0)} days
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Predictions Tab */}
          <TabsContent value="predictions">
            <Card>
              <CardHeader>
                <CardTitle>Liquidity Predictions</CardTitle>
                <CardDescription>AI-powered 7-day and 30-day forecasts with recommendations</CardDescription>
              </CardHeader>
              <CardContent>
                {predictions.length === 0 ? (
                  <div className="text-center py-12">
                    <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">No Predictions Available</h3>
                    <p className="text-muted-foreground mb-4">Generate predictions to see liquidity forecasts</p>
                    <Button onClick={handleGeneratePredictions} disabled={runningPredictions}>
                      <Play className="h-4 w-4 mr-2" />
                      Generate Predictions
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {predictions.map((prediction) => (
                      <Card key={prediction.portfolioId} className="border">
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-base capitalize">{prediction.portfolioId}</CardTitle>
                            <Badge className={
                              prediction.recommendation?.urgency === 'CRITICAL' ? 'bg-red-500' :
                              prediction.recommendation?.urgency === 'HIGH' ? 'bg-orange-500' : 'bg-emerald-500'
                            }>
                              {prediction.recommendation?.urgency || 'LOW'}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-muted-foreground">7-Day Scenario</p>
                              <p className="font-medium capitalize">{prediction.forecast7Day?.scenario || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">30-Day Scenario</p>
                              <p className="font-medium capitalize">{prediction.forecast30Day?.scenario || 'N/A'}</p>
                            </div>
                          </div>
                          <div className="pt-2 border-t">
                            <p className="text-muted-foreground text-sm">Recommendation</p>
                            <p className="font-medium text-sm">{prediction.recommendation?.action || 'MAINTAIN'}</p>
                            <p className="text-xs text-muted-foreground mt-1">{prediction.recommendation?.rationale}</p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rebalancing Tab */}
          <TabsContent value="rebalancing">
            <Card>
              <CardHeader>
                <CardTitle>Pending Rebalancing Actions</CardTitle>
                <CardDescription>Review and approve portfolio rebalancing recommendations</CardDescription>
              </CardHeader>
              <CardContent>
                {pendingRebalancing.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="h-12 w-12 mx-auto text-emerald-500 mb-4" />
                    <h3 className="text-lg font-medium mb-2">All Clear</h3>
                    <p className="text-muted-foreground">No pending rebalancing actions require approval</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Portfolio</TableHead>
                        <TableHead>Trigger</TableHead>
                        <TableHead>Action</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Approval Level</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingRebalancing.map((action) => (
                        <TableRow key={action.id}>
                          <TableCell className="font-medium capitalize">{action.portfolio_id}</TableCell>
                          <TableCell><Badge variant="outline">{action.trigger?.type || 'DRIFT'}</Badge></TableCell>
                          <TableCell>{action.recommendation?.action || 'N/A'}</TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(action.recommendation?.amount || 0)}</TableCell>
                          <TableCell>
                            <Badge className={
                              action.approval?.level === 'EXECUTIVE' ? 'bg-violet-500' :
                              action.approval?.level === 'MANAGER' ? 'bg-blue-500' : 'bg-emerald-500'
                            }>
                              {action.approval?.level || 'AUTO'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="ghost" onClick={() => { setSelectedAction(action); setActionDialogOpen(true); }}>
                              Review <ChevronRight className="h-4 w-4 ml-1" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          Last updated: {dashboardData?.summary?.lastUpdated ? new Date(dashboardData.summary.lastUpdated).toLocaleString() : 'N/A'}
        </div>
      </div>

      {/* Stress Month Simulator */}
      <StressMonthSimulator isOpen={stressOpen} onClose={() => setStressOpen(false)} />

      {/* Rebalancing Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Review Rebalancing Action</DialogTitle>
            <DialogDescription>Approve or reject this portfolio rebalancing recommendation</DialogDescription>
          </DialogHeader>
          
          {selectedAction && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Portfolio</p>
                  <p className="font-medium capitalize">{selectedAction.portfolio_id}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Trigger</p>
                  <p className="font-medium">{selectedAction.trigger?.type}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Drift</p>
                  <p className="font-medium">{formatPercent(selectedAction.trigger?.drift || 0)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Approval Level</p>
                  <Badge className={
                    selectedAction.approval?.level === 'EXECUTIVE' ? 'bg-violet-500' :
                    selectedAction.approval?.level === 'MANAGER' ? 'bg-blue-500' : 'bg-emerald-500'
                  }>
                    {selectedAction.approval?.level}
                  </Badge>
                </div>
              </div>
              
              <div className="border rounded-lg p-4 bg-muted/30">
                <p className="text-sm text-muted-foreground mb-2">Recommendation</p>
                <p className="font-medium">{selectedAction.recommendation?.action}</p>
                <p className="text-2xl font-bold mt-2">{formatCurrency(selectedAction.recommendation?.amount || 0)}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Estimated cost: {formatCurrency(selectedAction.recommendation?.estimatedTotalCost || 0)}
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => handleRejectRebalancing(selectedAction?.id)} className="text-red-600 hover:text-red-700">
              <XCircle className="h-4 w-4 mr-2" />
              Reject
            </Button>
            <Button onClick={() => handleApproveRebalancing(selectedAction?.id)} className="bg-emerald-600 hover:bg-emerald-700">
              <CheckCircle className="h-4 w-4 mr-2" />
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
